/**
 * DOM utility helpers for focus/input detection.
 *
 * Used by keyboard shortcut guards, DnD, and clipboard handlers
 * to determine whether the user is currently editing text.
 */

/** Tags that are always text inputs */
const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "password",
  "url",
  "tel",
  "number",
])

/**
 * Check if the given element is a contentEditable element
 * (includes TipTap / ProseMirror editors).
 */
export function isContentEditable(el: Element | null): boolean {
  if (!el) return false
  if ((el as HTMLElement).isContentEditable) return true
  if (el.getAttribute("contenteditable") === "true") return true
  if (el.getAttribute("role") === "textbox") return true
  // TipTap / ProseMirror
  if (el.closest(".ProseMirror") !== null) return true
  if (el.closest("[data-tiptap-editor]") !== null) return true
  return false
}

/**
 * Check if the given element is a text input (input, textarea, or contentEditable).
 *
 * Returns true for any element where the user is expected to be typing,
 * meaning single-char keyboard shortcuts should be suppressed.
 */
export function isTextInput(el: Element | null): boolean {
  if (!el) return false
  if (isContentEditable(el)) return true
  const tag = el.tagName
  if (tag === "TEXTAREA") return true
  if (tag === "INPUT") {
    const type = (el as HTMLInputElement).type?.toLowerCase() ?? "text"
    return TEXT_INPUT_TYPES.has(type)
  }
  return false
}

/**
 * Check if the currently focused element is a text input.
 * Convenience wrapper around `isTextInput(document.activeElement)`.
 */
export function isActiveElementTextInput(): boolean {
  return isTextInput(document.activeElement)
}
